import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { baseKeymap } from 'prosemirror-commands'
import { buildKeymap } from 'prosemirror-example-setup'
import { history, redo, undo } from 'prosemirror-history'
import { keymap } from 'prosemirror-keymap'
import type { EditorState as EditorStateType } from 'prosemirror-state'
import { EditorState, TextSelection } from 'prosemirror-state'
import { EditorView } from 'prosemirror-view'
import type { ArtifactEditorFormatType } from '@/analytics/enums/artifact-editor.enums'
import MatterArtifactSelectionPopover from '@/agent-v2/artifact-selection/components/MatterArtifactSelectionPopover'
import { useMatterArtifactSelectionTracking, type MatterArtifactSelectionRange } from '@/agent-v2/artifact-selection/hooks/useMatterArtifactSelectionTracking'
import type { MatterArtifactSelectionAttachment } from '@/agent-v2/artifact-selection/types'
import { canOpenAgentV2Reference } from '@/agent-v2/references/agent-v2-reference-opening'
import type { AgentV2Reference } from '@/agent-v2/store/schemas'
import { ArrowDown, ArrowUp } from '@/assets/icons'
import { kCitationIdRegex } from '@/util/agent-citations'
import { activeCitationHighlightKey, createActiveCitationHighlightPlugin } from '../prosemirror/active-citation-highlight'
import { AgentV2ReferenceMarkerView } from '../prosemirror/agent-v2-reference-marker-view'
import { clipboardSerializer, serializeClipboardText } from '../prosemirror/clipboard'
import { createInlineTreatmentFlagPlugin, inlineTreatmentFlagKey } from '../prosemirror/inline-treatment-flag-decorations'
import type { WarningCaseStatus } from '@/citator/schema/case-status-schema'
import {
  kMatterArtifactSelectionHighlightClass,
  matterArtifactSelectionHighlightKey,
  matterArtifactSelectionHighlightPlugin,
} from '../prosemirror/matter-artifact-selection-highlight'
import { markdownEditorSchema } from '../prosemirror/schema'
import { selectionCoveredByList, toggleList } from '../prosemirror/toggle-list'
import { EditorSaveStatusType, FormatProperty, TextSizeLabel, type EditorFormatState } from '../types'
import { kArtifactLegalCitationCanOpenAttr, kArtifactLegalCitationIdAttr } from '../utils/artifact-citation-helpers'
import { buildMatterArtifactSelection, matterArtifactSelectionMatchesDocument } from '../utils/build-matter-artifact-selection'
import { parseMarkdownDocument, serializeMarkdownDocument } from '../utils/editor-helpers'
import MarkdownEditorToolbar from './MarkdownEditorToolbar'
import './markdown-editor.css'
import './markdown-editor-toolbar.css'

const DEFAULT_FORMAT_STATE: EditorFormatState = {
  [FormatProperty.STRONG]: false,
  [FormatProperty.EM]: false,
  [FormatProperty.BULLET_LIST]: false,
  [FormatProperty.ORDERED_LIST]: false,
}

function getTextStyle(state: EditorStateType): TextSizeLabel {
  const parentNode = state.selection.$from.parent
  if (parentNode.type.name !== 'heading') return TextSizeLabel.NORMAL_TEXT

  switch (parentNode.attrs.level) {
    case 1:
      return TextSizeLabel.HEADING_1
    case 2:
      return TextSizeLabel.HEADING_2
    case 3:
      return TextSizeLabel.HEADING_3
    default:
      return TextSizeLabel.NORMAL_TEXT
  }
}

function getFormatState(state: EditorStateType): EditorFormatState {
  const { $from, from, to } = state.selection
  // Active only when the whole selection is in that list type, mirroring what a click does
  // (a partly covered selection gets the list applied rather than removed).
  const bulletListActive = selectionCoveredByList(state, markdownEditorSchema.nodes.bullet_list)
  const orderedListActive = selectionCoveredByList(state, markdownEditorSchema.nodes.ordered_list)

  return {
    [FormatProperty.STRONG]:
      from === to
        ? Boolean(markdownEditorSchema.marks.strong.isInSet(state.storedMarks ?? $from.marks()))
        : state.doc.rangeHasMark(from, to, markdownEditorSchema.marks.strong),
    [FormatProperty.EM]:
      from === to
        ? Boolean(markdownEditorSchema.marks.em.isInSet(state.storedMarks ?? $from.marks()))
        : state.doc.rangeHasMark(from, to, markdownEditorSchema.marks.em),
    [FormatProperty.BULLET_LIST]: bulletListActive,
    [FormatProperty.ORDERED_LIST]: orderedListActive,
  }
}

interface AgentV2MarkdownEditorProps {
  initialMarkdown: string
  onChange?: (markdown: string) => void
  /** Reports the markdown the document was last parsed from. Typed edits do not reparse; a new initialMarkdown or references map does. */
  onDocumentParsed?: (markdown: string) => void
  onBlur?: () => void
  readOnly?: boolean
  showMenu?: boolean
  saveStatus?: EditorSaveStatusType
  onRetry?: () => void
  referencesByCitationId?: Record<string, AgentV2Reference>
  /**
   * Whether onCitationClick opens sources in the draft workspace's chat panel rather than a new
   * browser tab. A source only the panel can show (a docket) renders unavailable without it.
   */
  referencesOpenInPanel?: boolean
  /** The citation whose source is open beside the document; its citations are highlighted. */
  activeCitationId?: string | null
  onCitationClick?: (citationId: string) => void
  /** Opens the citation's treatment detail from an inline flag; presence enables the flags. */
  onTreatmentFlagClick?: (citationId: string) => void
  /** Fired once per flagged citation when its flag first draws (inline-surface impression). */
  onTreatmentFlagImpression?: (citationId: string, caseStatus: WarningCaseStatus) => void
  matterArtifactId?: string
  matterArtifactVersionId?: string
  attachedMatterSelection?: MatterArtifactSelectionAttachment | null
  onSubmitMatterSelection?: (attachment: MatterArtifactSelectionAttachment, instruction: string) => boolean | Promise<boolean>
  onAttachedMatterSelectionInvalid?: () => void
  /** Optional trailing toolbar slot, rendered next to the save status (e.g. the Add Template button). */
  templateButton?: ReactNode
  /** Observational: forwarded to the toolbar to report which formatting command ran. */
  onFormatUsed?: (formatType: ArtifactEditorFormatType) => void
  /** Observational: reports a copy made from inside the editor. */
  onCopy?: () => void
}

export default function AgentV2MarkdownEditor({
  initialMarkdown,
  onChange,
  onDocumentParsed,
  onBlur,
  readOnly = false,
  showMenu = false,
  saveStatus,
  onRetry,
  referencesByCitationId,
  referencesOpenInPanel = false,
  activeCitationId = null,
  onCitationClick,
  onTreatmentFlagClick,
  onTreatmentFlagImpression,
  matterArtifactId,
  matterArtifactVersionId,
  attachedMatterSelection,
  onSubmitMatterSelection,
  onAttachedMatterSelectionInvalid,
  templateButton,
  onFormatUsed,
  onCopy,
}: AgentV2MarkdownEditorProps) {
  const editorElementRef = useRef<HTMLDivElement>(null)
  const editorScrollElementRef = useRef<HTMLDivElement | null>(null)
  const editorViewRef = useRef<EditorView | null>(null)
  const initialMarkdownRef = useRef(initialMarkdown)
  const initialReferencesByCitationIdRef = useRef(referencesByCitationId)
  const initialReferencesOpenInPanelRef = useRef(referencesOpenInPanel)
  const lastMarkdownRef = useRef(initialMarkdown)
  const lastReferencesByCitationIdRef = useRef(referencesByCitationId)
  const lastReferencesOpenInPanelRef = useRef(referencesOpenInPanel)
  const onChangeRef = useRef(onChange)
  const onCopyRef = useRef(onCopy)
  const onDocumentParsedRef = useRef(onDocumentParsed)
  const onBlurRef = useRef(onBlur)
  const activeCitationIdRef = useRef(activeCitationId)
  const onCitationClickRef = useRef(onCitationClick)
  const onTreatmentFlagClickRef = useRef(onTreatmentFlagClick)
  const onTreatmentFlagImpressionRef = useRef(onTreatmentFlagImpression)
  const onSubmitMatterSelectionRef = useRef(onSubmitMatterSelection)
  const onAttachedMatterSelectionInvalidRef = useRef(onAttachedMatterSelectionInvalid)
  const readOnlyRef = useRef(readOnly)
  const selectionPopoverDismissedRef = useRef(false)
  // Tracks which committed selection has been handed to the highlight plugin, so the effect below
  // dispatches coordinates exactly once per commit while the plugin keeps them mapped afterwards.
  const lastDispatchedCommittedSelectionRef = useRef<MatterArtifactSelectionRange | null | undefined>(undefined)
  const [editorInitialized, setEditorInitialized] = useState(false)
  const [currentTextStyle, setCurrentTextStyle] = useState(TextSizeLabel.NORMAL_TEXT)
  const [formatState, setFormatState] = useState<EditorFormatState>(DEFAULT_FORMAT_STATE)
  const [matterSelectionAnchorRect, setMatterSelectionAnchorRect] = useState<DOMRect | null>(null)
  const [attachedMatterSelectionDirection, setAttachedMatterSelectionDirection] = useState<'above' | 'below' | null>(null)
  const collapseProseMirrorSelection = useCallback(() => {
    const view = editorViewRef.current
    if (!view) return
    const selection = view.state.selection
    if (!(selection instanceof TextSelection) || selection.empty) return

    // Blur hides the browser selection without changing ProseMirror state. Collapse it so a later
    // toolbar action cannot modify text the user no longer sees as selected.
    view.dispatch(view.state.tr.setSelection(TextSelection.create(view.state.doc, selection.head)))
  }, [])
  const {
    clearSelection: clearCommittedMatterSelection,
    committedSelection: committedMatterSelection,
    containerRef: matterSelectionContainerRef,
    handleSelectionChange: handleMatterSelectionChange,
  } = useMatterArtifactSelectionTracking({ disabled: readOnly, onSelectionClickAway: collapseProseMirrorSelection })
  // dispatchTransaction is created once with the view; it reaches the latest anchor updater here.
  const updateMatterSelectionAnchorRectRef = useRef<(() => void) | null>(null)

  const updateMatterSelectionAnchorRect = useCallback(() => {
    const view = editorViewRef.current
    const scrollElement = editorScrollElementRef.current
    if (!view || !scrollElement || !committedMatterSelection || selectionPopoverDismissedRef.current) {
      setMatterSelectionAnchorRect(null)
      return
    }
    // The popover anchors to the plugin's mapped range, never to cached React positions: the
    // plugin remaps through every document change, so its head is in-bounds by construction and
    // vanishes exactly when the highlighted text does. Popover visibility stays independent so
    // formatting can remap the decoration without reopening a dismissed prompt.
    const highlightRange = matterArtifactSelectionHighlightKey.getState(view.state)?.range
    if (!highlightRange) {
      setMatterSelectionAnchorRect(null)
      return
    }

    const coords = view.coordsAtPos(highlightRange.head)
    const scrollRect = scrollElement.getBoundingClientRect()
    if (coords.bottom < scrollRect.top || coords.top > scrollRect.bottom) {
      setMatterSelectionAnchorRect(null)
      return
    }

    setMatterSelectionAnchorRect(new DOMRect(view.dom.getBoundingClientRect().left, coords.top, 0, coords.bottom - coords.top))
  }, [committedMatterSelection])
  updateMatterSelectionAnchorRectRef.current = updateMatterSelectionAnchorRect

  const updateAttachedMatterSelectionVisibility = useCallback(() => {
    const view = editorViewRef.current
    const scrollElement = editorScrollElementRef.current
    if (!view || !scrollElement || !attachedMatterSelection) {
      setAttachedMatterSelectionDirection(null)
      return
    }

    const highlights = view.dom.querySelectorAll<HTMLElement>(`.${kMatterArtifactSelectionHighlightClass}`)
    const firstHighlight = highlights.item(0)
    const lastHighlight = highlights.item(highlights.length - 1)
    if (!firstHighlight || !lastHighlight) {
      setAttachedMatterSelectionDirection(null)
      return
    }

    const scrollRect = scrollElement.getBoundingClientRect()
    const firstHighlightRect = firstHighlight.getBoundingClientRect()
    const lastHighlightRect = lastHighlight.getBoundingClientRect()
    if (lastHighlightRect.bottom < scrollRect.top) {
      setAttachedMatterSelectionDirection('above')
    } else if (firstHighlightRect.top > scrollRect.bottom) {
      setAttachedMatterSelectionDirection('below')
    } else {
      setAttachedMatterSelectionDirection(null)
    }
  }, [attachedMatterSelection])

  const scrollToAttachedMatterSelection = useCallback(() => {
    const view = editorViewRef.current
    const scrollElement = editorScrollElementRef.current
    const firstHighlight = view?.dom.querySelector<HTMLElement>(`.${kMatterArtifactSelectionHighlightClass}`)
    if (!scrollElement || !firstHighlight) return

    const scrollRect = scrollElement.getBoundingClientRect()
    const highlightRect = firstHighlight.getBoundingClientRect()
    const highlightTop = scrollElement.scrollTop + highlightRect.top - scrollRect.top
    scrollElement.scrollTo({ top: Math.max(0, highlightTop), behavior: 'smooth' })
  }, [])

  const setEditorScrollElement = useCallback(
    (node: HTMLDivElement | null) => {
      editorScrollElementRef.current = node
      matterSelectionContainerRef(node)
    },
    [matterSelectionContainerRef]
  )

  useEffect(() => {
    onChangeRef.current = onChange
    onCopyRef.current = onCopy
    onDocumentParsedRef.current = onDocumentParsed
    onBlurRef.current = onBlur
    onCitationClickRef.current = onCitationClick
    onTreatmentFlagClickRef.current = onTreatmentFlagClick
    onTreatmentFlagImpressionRef.current = onTreatmentFlagImpression
    onSubmitMatterSelectionRef.current = onSubmitMatterSelection
    onAttachedMatterSelectionInvalidRef.current = onAttachedMatterSelectionInvalid
    readOnlyRef.current = readOnly
  }, [
    onAttachedMatterSelectionInvalid,
    onBlur,
    onChange,
    onCopy,
    onCitationClick,
    onDocumentParsed,
    onTreatmentFlagClick,
    onTreatmentFlagImpression,
    onSubmitMatterSelection,
    readOnly,
  ])

  useEffect(() => {
    if (!editorElementRef.current || editorViewRef.current) return

    const state = EditorState.create({
      doc: parseMarkdownDocument(markdownEditorSchema, initialMarkdownRef.current, initialReferencesByCitationIdRef.current, {
        referencesOpenInPanel: initialReferencesOpenInPanelRef.current,
      }),
      schema: markdownEditorSchema,
      plugins: [
        history(),
        // Installed ahead of buildKeymap so the list shortcuts run the toggling command
        // rather than its wrap-only bindings.
        keymap({
          'Shift-Ctrl-8': toggleList(markdownEditorSchema.nodes.bullet_list, markdownEditorSchema.nodes.list_item),
          'Shift-Ctrl-9': toggleList(markdownEditorSchema.nodes.ordered_list, markdownEditorSchema.nodes.list_item),
        }),
        keymap(buildKeymap(markdownEditorSchema)),
        keymap(baseKeymap),
        keymap({
          'Mod-z': undo,
          'Mod-y': redo,
          'Mod-Shift-z': redo,
        }),
        matterArtifactSelectionHighlightPlugin,
        createActiveCitationHighlightPlugin({
          getActiveCitationId: () => activeCitationIdRef.current,
        }),
        // Always installed; renders nothing until the host wires a click handler
        // (chat-parity gating). Everything reads live refs so this one-time setup
        // effect stays off the prop dependencies.
        createInlineTreatmentFlagPlugin({
          isEnabled: () => onTreatmentFlagClickRef.current !== undefined,
          getReferencesByCitationId: () => lastReferencesByCitationIdRef.current,
          onFlagClick: (citationId) => onTreatmentFlagClickRef.current?.(citationId),
          onFlagImpression: (citationId, caseStatus) => onTreatmentFlagImpressionRef.current?.(citationId, caseStatus),
        }),
      ],
    })

    const view = new EditorView(editorElementRef.current, {
      state,
      // Copies substitute the readable [label] for the raw citation token (the schema's
      // toDOM keeps the token for the markdown round-trip; node views don't run on copy).
      clipboardSerializer,
      clipboardTextSerializer: serializeClipboardText,
      nodeViews: {
        referenceMarker: (node, _view, _getPosition, decorations) =>
          new AgentV2ReferenceMarkerView(
            node,
            (citationId) => {
              onBlurRef.current?.()
              onCitationClickRef.current?.(citationId)
            },
            decorations
          ),
      },
      dispatchTransaction(transaction) {
        const nextState = view.state.apply(transaction)
        view.updateState(nextState)
        setCurrentTextStyle(getTextStyle(nextState))
        setFormatState(getFormatState(nextState))
        // The highlight plugin remaps its range inside apply(); keep the popover anchored to the
        // mapped position (and hide it if the highlighted text was just replaced away).
        if (transaction.docChanged) updateMatterSelectionAnchorRectRef.current?.()
        const hasAttachableSelection =
          nextState.selection instanceof TextSelection &&
          !nextState.selection.empty &&
          nextState.doc.textBetween(nextState.selection.from, nextState.selection.to, ' ').trim().length > 0
        handleMatterSelectionChange(
          hasAttachableSelection
            ? {
                from: nextState.selection.from,
                to: nextState.selection.to,
                head: nextState.selection.head,
              }
            : null
        )

        if (transaction.docChanged && !transaction.getMeta('externalUpdate') && !readOnlyRef.current) {
          const markdown = serializeMarkdownDocument(markdownEditorSchema, nextState.doc)
          lastMarkdownRef.current = markdown
          onChangeRef.current?.(markdown)
        }
      },
      handleDOMEvents: {
        blur: () => {
          onBlurRef.current?.()
          return false
        },
        // The browser still performs the copy; stopPropagation only keeps the document-level
        // ClipboardAnalyticsListener from also counting this as a generic ClipboardCopy.
        // Returning false leaves the copy to ProseMirror's own handling.
        copy: (_editorView, event) => {
          event.stopPropagation()
          onCopyRef.current?.()
          return false
        },
        click: (_editorView, event) => {
          const target = event.target
          if (!(target instanceof Element)) return false

          const legalCitation = target.closest(`[${kArtifactLegalCitationIdAttr}]`)
          if (!legalCitation) return false

          const citationId = legalCitation.getAttribute(kArtifactLegalCitationIdAttr) ?? ''
          if (legalCitation.getAttribute(kArtifactLegalCitationCanOpenAttr) === 'true' && kCitationIdRegex.test(citationId)) {
            onBlurRef.current?.()
            onCitationClickRef.current?.(citationId)
          }
          return true
        },
      },
      editable: () => !readOnlyRef.current,
    })

    editorViewRef.current = view
    onDocumentParsedRef.current?.(initialMarkdownRef.current)
    setCurrentTextStyle(getTextStyle(view.state))
    setFormatState(getFormatState(view.state))
    setMatterSelectionAnchorRect(null)
    setEditorInitialized(true)

    return () => {
      view.destroy()
      editorViewRef.current = null
    }
  }, [handleMatterSelectionChange])

  useEffect(() => {
    const view = editorViewRef.current
    if (!view) return

    const markdownChanged = initialMarkdown !== lastMarkdownRef.current
    const referencesChanged = referencesByCitationId !== lastReferencesByCitationIdRef.current
    // Crossing the narrow-screen breakpoint changes which citations can open.
    const openSurfaceChanged = referencesOpenInPanel !== lastReferencesOpenInPanelRef.current
    if (!markdownChanged && !referencesChanged && !openSurfaceChanged) return

    if (openSurfaceChanged && !markdownChanged && !referencesChanged) {
      // Only openability changed, so the citations' canOpen attributes update in place instead of
      // a reparse; the caret, the selected-passage highlight and undo history stay intact.
      const canOpen = (citationId: string) => {
        const reference = referencesByCitationId?.[citationId]
        return Boolean(reference && canOpenAgentV2Reference(reference, { opensInPanel: referencesOpenInPanel }))
      }
      const legalCitationType = markdownEditorSchema.marks.legalCitation
      const tr = view.state.tr
      view.state.doc.descendants((node, pos) => {
        if (node.type === markdownEditorSchema.nodes.referenceMarker) {
          const nextCanOpen = canOpen(node.attrs.citationId)
          if (node.attrs.canOpen !== nextCanOpen) tr.setNodeAttribute(pos, 'canOpen', nextCanOpen)
          return false
        }

        const legalCitation = node.isText ? legalCitationType.isInSet(node.marks) : undefined
        if (!legalCitation) return
        const nextCanOpen = canOpen(legalCitation.attrs.citationId)
        if (legalCitation.attrs.canOpen !== nextCanOpen) {
          tr.addMark(pos, pos + node.nodeSize, legalCitationType.create({ ...legalCitation.attrs, canOpen: nextCanOpen }))
        }
      })
      lastReferencesOpenInPanelRef.current = referencesOpenInPanel
      if (tr.docChanged) view.dispatch(tr.setMeta('addToHistory', false).setMeta('externalUpdate', true))
      return
    }

    const nextDocument = parseMarkdownDocument(markdownEditorSchema, initialMarkdown, referencesByCitationId, { referencesOpenInPanel })
    lastMarkdownRef.current = initialMarkdown
    lastReferencesByCitationIdRef.current = referencesByCitationId
    lastReferencesOpenInPanelRef.current = referencesOpenInPanel
    onDocumentParsedRef.current?.(initialMarkdown)
    if (view.state.doc.eq(nextDocument)) {
      // The doc can be unchanged while the references still carry new information the
      // flag decorations render (marks don't encode case_status): e.g. citator
      // enrichment landing after the initial reference write. Nudge the flag plugin
      // so a late status change shows/clears its flag without waiting for a keystroke.
      if (referencesChanged) view.dispatch(view.state.tr.setMeta(inlineTreatmentFlagKey, 'references-changed').setMeta('addToHistory', false))
      return
    }

    if (markdownChanged) {
      const nextState = EditorState.create({
        doc: nextDocument,
        schema: markdownEditorSchema,
        plugins: view.state.plugins,
      })
      view.updateState(nextState)
      setCurrentTextStyle(getTextStyle(nextState))
      setFormatState(getFormatState(nextState))
      clearCommittedMatterSelection()
      setMatterSelectionAnchorRect(null)
      return
    }

    view.dispatch(
      view.state.tr.replaceWith(0, view.state.doc.content.size, nextDocument.content).setMeta('addToHistory', false).setMeta('externalUpdate', true)
    )
  }, [clearCommittedMatterSelection, initialMarkdown, referencesByCitationId, referencesOpenInPanel])

  // The ref is the plugin's own source of truth (it rebuilds from it whenever editor state is
  // recreated), so it is kept current even before there is a view to nudge.
  useEffect(() => {
    if (activeCitationIdRef.current === activeCitationId) return
    activeCitationIdRef.current = activeCitationId

    const view = editorViewRef.current
    if (!view) return
    view.dispatch(view.state.tr.setMeta(activeCitationHighlightKey, 'active-citation-changed').setMeta('addToHistory', false))
  }, [activeCitationId])

  useEffect(() => {
    editorViewRef.current?.setProps({
      editable: () => !readOnly,
    })
  }, [readOnly])

  useEffect(() => {
    const view = editorViewRef.current
    if (!view) return

    if (attachedMatterSelection) {
      if (
        attachedMatterSelection.selection.matter_artifact_id !== matterArtifactId ||
        attachedMatterSelection.selection.matter_artifact_version_id !== matterArtifactVersionId ||
        !matterArtifactSelectionMatchesDocument({
          attachment: attachedMatterSelection,
          document: view.state.doc,
          schema: markdownEditorSchema,
          sourceMarkdown: lastMarkdownRef.current,
        })
      ) {
        view.dispatch(view.state.tr.setMeta(matterArtifactSelectionHighlightKey, null))
        onAttachedMatterSelectionInvalidRef.current?.()
        return
      }

      view.dispatch(view.state.tr.setMeta(matterArtifactSelectionHighlightKey, attachedMatterSelection.editorRange))
      lastDispatchedCommittedSelectionRef.current = undefined
      return
    }

    // Dispatch committed coordinates only when a commit actually happened — they are fresh from
    // the live selection at that moment. Between commits the plugin owns the range and remaps it
    // through every document change, so re-sending cached React positions here would only
    // re-introduce staleness on unrelated dependency churn.
    if (lastDispatchedCommittedSelectionRef.current === committedMatterSelection) return
    lastDispatchedCommittedSelectionRef.current = committedMatterSelection
    view.dispatch(
      view.state.tr.setMeta(
        matterArtifactSelectionHighlightKey,
        committedMatterSelection ? { from: committedMatterSelection.from, to: committedMatterSelection.to, head: committedMatterSelection.head } : null
      )
    )
  }, [attachedMatterSelection, committedMatterSelection, initialMarkdown, matterArtifactId, matterArtifactVersionId, referencesByCitationId])

  useEffect(() => {
    selectionPopoverDismissedRef.current = false
    updateMatterSelectionAnchorRect()
    if (!committedMatterSelection) return

    window.addEventListener('resize', updateMatterSelectionAnchorRect)
    return () => window.removeEventListener('resize', updateMatterSelectionAnchorRect)
  }, [committedMatterSelection, updateMatterSelectionAnchorRect])

  useEffect(() => {
    updateAttachedMatterSelectionVisibility()
    if (!attachedMatterSelection) return

    window.addEventListener('resize', updateAttachedMatterSelectionVisibility)
    return () => window.removeEventListener('resize', updateAttachedMatterSelectionVisibility)
  }, [attachedMatterSelection, updateAttachedMatterSelectionVisibility])

  const handleSubmitMatterSelection = async (instruction: string): Promise<boolean> => {
    const view = editorViewRef.current
    const onSubmit = onSubmitMatterSelectionRef.current
    // Submit against the plugin's mapped range: it has followed every document change since the
    // commit, whereas the React copy is frozen at mouseup and may predate edits.
    const highlightRange = view ? matterArtifactSelectionHighlightKey.getState(view.state)?.range : null
    if (!view || !onSubmit || !committedMatterSelection || !highlightRange || !matterArtifactId || !matterArtifactVersionId) return false

    const result = buildMatterArtifactSelection({
      document: view.state.doc,
      schema: markdownEditorSchema,
      sourceMarkdown: lastMarkdownRef.current,
      matterArtifactId,
      matterArtifactVersionId,
      from: highlightRange.from,
      to: highlightRange.to,
    })
    if (!result) return false

    view.dispatch(view.state.tr.setMeta(matterArtifactSelectionHighlightKey, result.attachment.editorRange))
    view.setProps({ editable: () => false })

    let submitted = false
    try {
      submitted = await onSubmit(result.attachment, instruction)
    } catch {
      submitted = false
    }

    if (editorViewRef.current !== view) return submitted
    view.setProps({ editable: () => !readOnlyRef.current })
    if (submitted) {
      clearCommittedMatterSelection()
      view.dispatch(
        view.state.tr
          .setSelection(TextSelection.create(view.state.doc, result.attachment.editorRange.to))
          .setMeta(matterArtifactSelectionHighlightKey, result.attachment.editorRange)
      )
      window.getSelection()?.removeAllRanges()
      return true
    }

    return false
  }

  const closeMatterSelectionPopover = () => {
    // Radix closes before document mousedown. Keep selection state here so the global handler can
    // distinguish a formatting control from a true clickaway.
    selectionPopoverDismissedRef.current = true
    setMatterSelectionAnchorRect(null)
  }

  const clearMatterSelection = () => {
    selectionPopoverDismissedRef.current = false
    clearCommittedMatterSelection()
    setMatterSelectionAnchorRect(null)
  }

  return (
    <div className="prose-editor-wrapper relative">
      {showMenu && editorInitialized && (!readOnly || saveStatus === EditorSaveStatusType.CONFLICT) && (
        <MarkdownEditorToolbar
          editorView={editorViewRef.current}
          schema={markdownEditorSchema}
          saveStatus={saveStatus}
          onRetry={onRetry}
          currentTextStyle={currentTextStyle}
          formatState={formatState}
          readOnly={readOnly}
          templateButton={templateButton}
          onFormatUsed={onFormatUsed}
        />
      )}
      <div
        ref={setEditorScrollElement}
        className="prose-editor-container bg-white p-2 h-full overflow-y-auto flex justify-center"
        onScroll={() => {
          updateMatterSelectionAnchorRect()
          updateAttachedMatterSelectionVisibility()
        }}
      >
        <div ref={editorElementRef} className={`prose-mirror-editor prose max-w-4xl w-full ${readOnly ? 'opacity-75' : ''}`} aria-readonly={readOnly} />
      </div>
      {!readOnly && committedMatterSelection && matterSelectionAnchorRect && matterArtifactId && matterArtifactVersionId && onSubmitMatterSelection ? (
        <MatterArtifactSelectionPopover
          anchorRect={matterSelectionAnchorRect}
          onDismiss={closeMatterSelectionPopover}
          onEscape={clearMatterSelection}
          onSubmit={handleSubmitMatterSelection}
        />
      ) : null}
      {attachedMatterSelectionDirection ? (
        <button
          className="absolute bottom-6 left-1/2 z-10 flex -translate-x-1/2 items-center gap-1 rounded-md bg-brand-neutral-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-neutral-700"
          onClick={scrollToAttachedMatterSelection}
          type="button"
        >
          {attachedMatterSelectionDirection === 'above' ? <ArrowUp className="size-[18px]" /> : <ArrowDown className="size-[18px]" />}
          Jump to changes
        </button>
      ) : null}
    </div>
  )
}
